import {
  Alert,
  AutoComplete,
  Avatar,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Dropdown,
  Form,
  Input,
  Layout,
  Menu,
  Modal,
  Row,
  Select,
  Skeleton,
  Spin,
  Statistic,
  Table,
  Tag,
  Timeline,
  Typography,
  Upload,
  message,
} from "antd";
import {
  BarChartOutlined,
  CalendarOutlined,
  CheckOutlined,
  CustomerServiceOutlined,
  DashboardOutlined,
  DollarOutlined,
  EditOutlined,
  FileTextOutlined,
  GlobalOutlined,
  HistoryOutlined,
  IdcardOutlined,
  LogoutOutlined,
  MedicineBoxOutlined,
  MessageOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  PrinterOutlined,
  TeamOutlined,
  UploadOutlined,
  UserAddOutlined,
  UserOutlined,
} from "@ant-design/icons";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ChangePasswordForm from "../../components/ChangePasswordForm";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useModules } from "../../context/ModuleContext";
import {
  adoptPatient,
  createBillingRecord,
  createDiagnosis,
  createPrescription,
  enrollPatient,
  getAdoptedPatients,
  getBillingRecords,
  getDiagnoses,
  getDoctorProfile,
  getPatientEnrollments,
  getPatientHistory,
  getPatientProfiles,
  getPharmacies,
  getPrescriptions,
  getSharedWithMe,
  registerPatient,
  updateDiagnosis,
} from "../../services/doctorService";
import {
  getMonthlyInvoices,
  getUsageSummary,
} from "../../services/usageBillingService";
import { createTicket, getMyTickets } from "../../services/supportService";
import PatientLookup from "../doctor/PatientLookup";
import PeerConsultation from "../doctor/PeerConsultation";
import Appointments from "../doctor/Appointments";
import NotificationBell from "../../components/NotificationBell";
import { handleApiError, showInactivePatientNotice } from "../../utils/apiErrors";

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

const toArray = (d) => Array.isArray(d) ? d : (d?.results ?? []);
const formatMonth = (m) => {
  if (!m) return "—";
  const [year, month] = m.split("-");
  return new Date(year, month - 1).toLocaleString("default", { month: "long", year: "numeric" });
};
const toCount = (d) => Array.isArray(d) ? d.length : (d?.count ?? d?.results?.length ?? 0); // eslint-disable-line no-unused-vars
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString() : "—";

const FREQ_LABELS = {
  ONCE_DAILY:        "Once Daily",
  TWICE_DAILY:       "Twice Daily",
  THREE_TIMES_DAILY: "Three Times Daily",
  FOUR_TIMES_DAILY:  "Four Times Daily",
  AS_NEEDED:         "As Needed",
};


const makeEnrollmentCols = (onViewHistory, onIssuePrescription, profileByEmail = {}) => [
  { title: "Patient Email", dataIndex: "patient_email", key: "patient_email",
    render: (e, r) => {
      const inactive = r.patient_is_active === false;
      return (
        <span>
          {e ?? <Text type="secondary">—</Text>}
          {inactive && (
            <Tag style={{ marginLeft: 8 }}>Inactive</Tag>
          )}
        </span>
      );
    }},
  { title: "Enrolled",      dataIndex: "enrolled_at",   key: "enrolled_at",   width: 120, render: fmtDate },
  { title: "Status",        dataIndex: "is_active",     key: "is_active",     width: 90,
    render: v => v ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag> },
  { title: "ID Status",     key: "id_status",           width: 130,
    render: (_, r) => {
      const prof = profileByEmail[r.patient_email];
      if (!prof) return <Tag>No ID</Tag>;
      if (prof.government_id_verified) return <Tag color="green">ID Verified</Tag>;
      if (prof.government_id_number)   return <Tag color="orange">ID Pending</Tag>;
      return <Tag>No ID</Tag>;
    }},
  { title: "Actions",       key: "actions",             width: 260,
    render: (_, r) => {
      const inactive = r.patient_is_active === false;
      const patientId = profileByEmail[r.patient_email]?.id ?? r.patient_profile_id ?? r.patient;
      return (
        <span>
          <Button size="small" icon={<HistoryOutlined />}
            onClick={() => onViewHistory(patientId)}>
            Full History
          </Button>
          <Button
            size="small"
            icon={<MedicineBoxOutlined />}
            style={{ marginLeft: 8 }}
            disabled={inactive}
            title={inactive ? "Patient account is inactive — reactivate through Administration to prescribe." : "Issue Prescription"}
            onClick={() => onIssuePrescription?.(patientId)}
          >
            Issue Rx
          </Button>
        </span>
      );
    }},
];

const makeRxCols = (emailMap, doctorProfile) => [
  { title: "Patient",    dataIndex: "patient",         key: "patient",
    render: id => emailMap[id]
      ? <Text>{emailMap[id]}</Text>
      : <Text code>{String(id).slice(0, 8)}…</Text> },
  { title: "Medication", dataIndex: "medication_name", key: "medication_name",
    render: v => {
      if (!v) return <Text type="secondary">—</Text>;
      const display = v.includes("{") || v.match(/^\d/) ? extractCleanDrugName(v) : v;
      return display || v;
    } },
  { title: "Dosage",     dataIndex: "dosage",          key: "dosage",         width: 100 },
  { title: "Frequency",  dataIndex: "frequency",       key: "frequency",      width: 130,
    render: f => f ? <Tag>{FREQ_LABELS[f] ?? f}</Tag> : <Text type="secondary">—</Text> },
  { title: "Pharmacy",   key: "pharmacy_name",         width: 150,
    render: (_, r) => r.pharmacy_name
      ? <Text style={{ color: "#52c41a" }}>{r.pharmacy_name}</Text>
      : <Text type="secondary">Network (Any)</Text> },
  { title: "Issued",     dataIndex: "created_at",      key: "created_at",     width: 110, render: fmtDate },
  { title: "Status",     dataIndex: "is_dispensed",    key: "is_dispensed",   width: 100,
    render: v => v ? <Tag color="blue">Dispensed</Tag> : <Tag color="orange">Pending</Tag> },
  { title: "Print",      key: "print",                 width: 75,
    render: (_, r) => (
      <Button size="small" icon={<PrinterOutlined />}
        onClick={() => printPrescription(r, doctorProfile)}>
        Print
      </Button>
    )},
];

const BILLING_COLS = [ // eslint-disable-line no-unused-vars
  { title: "Service Date",  dataIndex: "service_date",      key: "service_date",      width: 120, render: fmtDate },
  { title: "Patient",       dataIndex: "patient_email",     key: "patient_email",     render: e => e ?? "—" },
  { title: "Amount Billed", dataIndex: "amount_billed",     key: "amount_billed",     width: 130,
    render: a => a != null ? `$${parseFloat(a).toFixed(2)}` : "—" },
  { title: "Payor",         dataIndex: "payor_type",        key: "payor_type",        width: 110,
    render: t => t ? <Tag>{t}</Tag> : "—" },
  { title: "Status",        dataIndex: "submission_status", key: "submission_status", width: 110,
    render: s => {
      const c = { DRAFT: "default", SUBMITTED: "blue", APPROVED: "green", REJECTED: "red", PAID: "cyan" };
      return <Tag color={c[s] ?? "default"}>{s ?? "—"}</Tag>;
    } },
];

const SEVERITY_COLOR = { MILD: "green", MODERATE: "orange", SEVERE: "red", CRITICAL: "volcano" };

/**
 * Extract a clean generic drug name from a full RxNorm clinical name.
 *
 * RxNorm names look like:
 *   "24 HR metformin hydrochloride 500 MG Extended Release Oral Tablet"
 *   "4 ML amoxicillin 50 MG/ML Oral Suspension"
 *   "amoxicillin 500 MG Oral Capsule"
 *   "Amoxicillin"
 *
 * We want just the simple name: "Metformin", "Amoxicillin", etc.
 */
const extractCleanDrugName = (fullName) => {
  if (!fullName || typeof fullName !== "string") return "";
  let clean = fullName
    // Strip any stray template / brace artifacts e.g. "{4 " or "${var}"
    .replace(/\$?\{[^}]*\}?/g, "")
    // Strip leading quantity prefix: "24 HR ", "4 ML ", "10 MG " etc.
    .replace(/^\d+(\.\d+)?\s*(HR|ML|MG|MCG|UNIT|IU|MEQ)\s+/i, "")
    // Strip dosage and everything following it: " 500 MG Oral Tablet"
    .replace(/\s+\d+(\.\d+)?\s*(MG|ML|MCG|UNIT|IU|MEQ|HR|%).*/i, "")
    // Strip common salt/ester qualifiers that follow the INN
    .replace(/\s+(hydrochloride|hcl|sodium|potassium|sulfate|phosphate|maleate|tartrate|acetate|citrate|fumarate|succinate|bromide|mesylate|besylate|gluconate)\b.*/i, "")
    // Strip bracketed and parenthetical content
    .replace(/\s*\[.*?\]/g, "")
    .replace(/\s*\(.*?(\)|$)/g, "")
    .trim();
  if (!clean) {
    // Ultimate fallback: first word before any space or punctuation
    clean = (fullName.replace(/[{}$]/g, "").match(/[A-Za-z]+/) ?? [""])[0];
  }
  return (clean.charAt(0).toUpperCase() + clean.slice(1)).slice(0, 100);
};

const COMMON_MED_OPTIONS = [
  "Amoxicillin", "Metformin", "Lisinopril", "Atorvastatin",
  "Amlodipine", "Omeprazole", "Paracetamol", "Ibuprofen",
].map(n => ({ value: n, label: n, rxcui: null }));

// ── MedicationCard — isolated component so RxNorm search only re-renders that row ──
const MedicationCard = React.memo(function MedicationCard({
  fieldKey, name, index, totalFields, restField, rxForm, onRemove,
}) {
  const [options,      setOptions]      = useState(COMMON_MED_OPTIONS);
  const [searching,    setSearching]    = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  const timer = useRef(null);

  // Clean up any pending timer on unmount
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const handleSearch = useCallback((value) => {
    setSelectedCode(null);
    rxForm.setFieldValue(["medications", name, "rxnorm_code"], "");
    if (timer.current) clearTimeout(timer.current);
    if (!value || value.length < 3) {
      const filtered = value
        ? COMMON_MED_OPTIONS.filter(o => o.value.toLowerCase().startsWith(value.toLowerCase()))
        : COMMON_MED_OPTIONS;
      setOptions(filtered.length ? filtered : COMMON_MED_OPTIONS);
      return;
    }
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { searchDrugs } = await import("../../services/rxNormService");
        const drugs = await searchDrugs(value);
        const seen = new Set();
        const mapped = [];
        drugs.forEach(d => {
          const clean = extractCleanDrugName(d.name);
          if (!clean || seen.has(clean.toLowerCase())) return;
          seen.add(clean.toLowerCase());
          mapped.push({ value: clean, label: clean, rxcui: d.rxcui });
        });
        setOptions(mapped.length ? mapped.slice(0, 15) : COMMON_MED_OPTIONS);
      } catch {
        setOptions(COMMON_MED_OPTIONS); // RxNorm unreachable — fall back to common meds
      } finally {
        setSearching(false);
      }
    }, 800);
  }, [name, rxForm]);

  const handleSelect = useCallback((value, option) => {
    setSelectedCode(option.rxcui ?? null);
    // value is already the clean display name from our mapped options
    rxForm.setFieldValue(["medications", name, "medication_name"], value);
    rxForm.setFieldValue(["medications", name, "rxnorm_code"], option.rxcui ?? "");
  }, [name, rxForm]);

  const handleFocus = useCallback(() => {
    const current = rxForm.getFieldValue(["medications", name, "medication_name"]);
    if (!current) setOptions(COMMON_MED_OPTIONS);
  }, [name, rxForm]);

  return (
    <Card key={fieldKey} size="small" style={{ marginBottom: 12, background: "#fafafa", borderRadius: 6 }}
      title={<Text style={{ fontSize: 13, fontWeight: 600 }}>Medication #{index + 1}</Text>}
      extra={
        totalFields > 1 ? (
          <Button type="text" danger size="small" icon={<MinusCircleOutlined />} onClick={onRemove}>
            Remove
          </Button>
        ) : null
      }>
      {/* Hidden field — stores the RxNorm code for submission */}
      <Form.Item {...restField} name={[name, "rxnorm_code"]} hidden><Input /></Form.Item>

      <Row gutter={12}>
        <Col span={14}>
          <Form.Item {...restField} name={[name, "medication_name"]} label="Medication Name"
            rules={[{ required: true, message: "Required" }]}>
            <AutoComplete
              options={options}
              onSearch={handleSearch}
              onSelect={handleSelect}
              onFocus={handleFocus}
              filterOption={false}
              notFoundContent={null}
              getPopupContainer={trigger => trigger.parentElement}
            >
              <Input
                placeholder="Click to see common meds or type to search"
                suffix={searching ? <Spin size="small" /> : null}
              />
            </AutoComplete>
          </Form.Item>
          {selectedCode && (
            <div style={{ marginTop: -20, marginBottom: 8 }}>
              <Tag color="blue" style={{ fontSize: 11 }}>RxNorm: {selectedCode}</Tag>
            </div>
          )}
        </Col>
        <Col span={10}>
          <Form.Item {...restField} name={[name, "dosage"]} label="Dosage"
            rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="e.g. 500mg" />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item {...restField} name={[name, "frequency"]} label="Frequency"
            rules={[{ required: true, message: "Required" }]}>
            <Select placeholder="Select frequency">
              <Option value="ONCE_DAILY">Once Daily</Option>
              <Option value="TWICE_DAILY">Twice Daily</Option>
              <Option value="THREE_TIMES_DAILY">Three Times Daily</Option>
              <Option value="FOUR_TIMES_DAILY">Four Times Daily</Option>
              <Option value="AS_NEEDED">As Needed</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item {...restField} name={[name, "duration"]} label="Duration (optional)">
            <Input placeholder="e.g. 7 days, 1 month" />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item {...restField} name={[name, "instructions"]} label="Instructions (optional)"
        style={{ marginBottom: 0 }}>
        <Input.TextArea rows={2} placeholder="e.g. Take with food after meals" />
      </Form.Item>
    </Card>
  );
});

// ── Print helpers ─────────────────────────────────────────────────────────────

function buildSlipHtml(rx, doctorProfile) {
  const freqLabels = {
    ONCE_DAILY: "Once Daily", TWICE_DAILY: "Twice Daily",
    THREE_TIMES_DAILY: "Three Times Daily", FOUR_TIMES_DAILY: "Four Times Daily",
    AS_NEEDED: "As Needed",
  };
  const rxDate    = rx.created_at ? new Date(rx.created_at).toLocaleDateString() : "—";
  const rxId      = rx.id ? String(rx.id).slice(0, 8).toUpperCase() : "—";
  const status    = rx.is_dispensed ? "DISPENSED" : "PENDING";
  const freq      = rx.frequency_display ?? (freqLabels[rx.frequency] ?? rx.frequency ?? "—");
  const docName   = rx.doctor_name ?? doctorProfile?.user_email ?? "—";
  const license   = doctorProfile?.license_number ?? "—";
  return `
    <div class="header">
      <h1>MCHUSA Digital Healthcare Network</h1>
      <h2>Official Prescription Document</h2>
    </div>
    <div class="meta">
      <span><b>Date:</b> ${rxDate}</span>
      <span><b>Rx&nbsp;#:</b> ${rxId}</span>
      <span><b>Status:</b> <span class="badge">${status}</span></span>
    </div>
    <div class="section">
      <div class="stitle">Patient Information</div>
      <div class="field"><b>Name:</b> ${rx.patient_name ?? "—"}</div>
      <div class="field"><b>Patient PIN</b> <em>(present at pharmacy or kiosk to collect medication)</em>:<br/>
        <div class="pin-box"><div class="pin">${rx.patient_pin ?? "— — — —"}</div></div>
      </div>
    </div>
    <div class="section">
      <div class="stitle">Prescribed By</div>
      <div class="field"><b>Doctor:</b> ${docName}</div>
      <div class="field"><b>License No.:</b> ${license}</div>
    </div>
    <div class="section">
      <div class="stitle">Medication</div>
      <div class="field"><b>Drug:</b> ${rx.medication_name ?? "—"}</div>
      ${rx.rxnorm_code ? `<div class="field"><b>RxNorm Code:</b> ${rx.rxnorm_code}</div>` : ""}
      <div class="field"><b>Dosage:</b> ${rx.dosage ?? "—"}</div>
      <div class="field"><b>Frequency:</b> ${freq}</div>
      <div class="field"><b>Duration:</b> ${rx.duration || "—"}</div>
      <div class="field"><b>Instructions:</b> ${rx.instructions || "As directed by physician."}</div>
    </div>
    <div class="section">
      <div class="stitle">Dispensing Information</div>
      <div class="field">This prescription is valid at any MCHUSA network pharmacy.</div>
      <div class="field">Present your Patient PIN at any participating pharmacy or kiosk to collect medication.</div>
    </div>
    <div class="footer">* Official MCHUSA DHN Prescription &nbsp;|&nbsp; HIPAA Compliant</div>`;
}

const PRINT_CSS = `
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:'Times New Roman',serif; font-size:12pt; color:#000; background:#fff; }
  .slip { padding:20mm 20mm; }
  .page-break { page-break-after:always; }
  .header { text-align:center; border-bottom:2px solid #000; padding-bottom:10px; margin-bottom:14px; }
  .header h1 { font-size:16pt; font-weight:bold; letter-spacing:1px; }
  .header h2 { font-size:11pt; font-weight:normal; margin-top:3px; }
  .meta { display:flex; justify-content:space-between; font-size:10pt; margin-bottom:14px; }
  .section { margin-bottom:14px; }
  .stitle { font-size:10pt; font-weight:bold; text-transform:uppercase; letter-spacing:1px; border-bottom:1px solid #000; padding-bottom:3px; margin-bottom:8px; }
  .field { margin-bottom:5px; font-size:11pt; }
  .pin-box { border:2px solid #000; display:inline-block; padding:8px 16px; margin-top:4px; }
  .pin { font-size:24pt; font-family:monospace; font-weight:bold; letter-spacing:8px; }
  .badge { border:1px solid #000; padding:2px 8px; font-weight:bold; font-size:10pt; }
  .footer { border-top:1px solid #000; margin-top:16px; padding-top:8px; font-size:9pt; text-align:center; }
  @media print { .slip { padding:10mm 15mm; } }`;

function printPrescription(rx, doctorProfile) {
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>Rx — ${rx.id ? String(rx.id).slice(0,8).toUpperCase() : "DHN"}</title>
    <style>${PRINT_CSS}</style></head>
    <body><div class="slip">${buildSlipHtml(rx, doctorProfile)}</div>
    <script>window.onload=function(){window.print();window.onafterprint=function(){window.close();};};</script>
    </body></html>`;
  const win = window.open("", "_blank", "width=820,height=960");
  if (win) { win.document.write(html); win.document.close(); }
}

function printAllPrescriptions(prescriptions, doctorProfile) {
  if (!prescriptions.length) return;
  const slips = prescriptions.map((rx, i) =>
    `${i > 0 ? '<div class="page-break"></div>' : ""}<div class="slip">${buildSlipHtml(rx, doctorProfile)}</div>`
  ).join("");
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>All Prescriptions — MCHUSA DHN</title>
    <style>${PRINT_CSS}</style></head>
    <body>${slips}
    <script>window.onload=function(){window.print();window.onafterprint=function(){window.close();};};</script>
    </body></html>`;
  const win = window.open("", "_blank", "width=820,height=960");
  if (win) { win.document.write(html); win.document.close(); }
}

export default function DoctorDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasModule, hasFullAccess, organisation } = useModules();

  const [collapsed,    setCollapsed]    = useState(false);
  const [selectedMenu, setSelectedMenu] = useState("dashboard");
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [retryCount,   setRetryCount]   = useState(0);

  const [enrollments,   setEnrollments]   = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [billingRows,   setBillingRows]   = useState([]); // eslint-disable-line no-unused-vars
  const [doctorProfile, setDoctorProfile] = useState(null);
  const [stats, setStats] = useState({
    totalPatients: 0, activeEnrollments: 0, prescriptionsIssued: 0, billingRecords: 0,
  });

  const [patientProfiles,     setPatientProfiles]     = useState([]);
  const [profilesLoading,     setProfilesLoading]     = useState(false);
  const [profileEmailMap,     setProfileEmailMap]     = useState({});
  const [pharmacies,          setPharmacies]          = useState([]);

  const [usageInvoices,  setUsageInvoices]  = useState([]);
  const [usageSummary,   setUsageSummary]   = useState(null);
  const [usageLoaded,    setUsageLoaded]    = useState(false);
  const [usageLoading,   setUsageLoading]   = useState(false);

  const [invoiceDetailOpen,   setInvoiceDetailOpen]    = useState(false);
  const [invoiceDetailTarget, setInvoiceDetailTarget]  = useState(null);

  // ── diagnoses section state ───────────────────────────────────────────────────
  const [selectedPatientProfileId, setSelectedPatientProfileId] = useState(null);
  const [diagnoses,                setDiagnoses]                = useState([]);
  const [diagnosesLoading,         setDiagnosesLoading]         = useState(false);
  const [diagnosisModal,           setDiagnosisModal]           = useState(false);
  const [editingDiagnosis,         setEditingDiagnosis]         = useState(null);
  const [historyModal,             setHistoryModal]             = useState(false);
  const [historyData,              setHistoryData]              = useState(null);
  const [historyLoading,           setHistoryLoading]           = useState(false);
  const [icd10Options,             setIcd10Options]             = useState([]);
  const [icd10Searching,           setIcd10Searching]           = useState(false);
  const icd10Timer = useRef(null);

  // modal open states
  const [regModal,     setRegModal]     = useState(false);
  const [enrollModal,  setEnrollModal]  = useState(false);
  const [rxModal,      setRxModal]      = useState(false);
  const [billingModal, setBillingModal] = useState(false);
  const [submitting,   setSubmitting]   = useState(false);

  const [regForm]     = Form.useForm();
  const [enrollForm]  = Form.useForm();
  const [rxForm]      = Form.useForm();
  const [billingForm] = Form.useForm();
  const [dxForm]      = Form.useForm();
  const [ticketForm]  = Form.useForm();

  // support state
  const [myTickets,      setMyTickets]      = useState([]);
  const [ticketModal,    setTicketModal]    = useState(false);
  const [ticketDetail,   setTicketDetail]   = useState(null);
  const [supportLoaded,  setSupportLoaded]  = useState(false);
  const [supportLoading, setSupportLoading] = useState(false);

  // adopted patients state
  const [adoptedPatients,    setAdoptedPatients]    = useState([]);
  const [adoptedLoaded,      setAdoptedLoaded]      = useState(false);
  const [adoptedLoading,     setAdoptedLoading]     = useState(false);
  const [sharedWithMe,       setSharedWithMe]       = useState([]);
  const [adoptModal,         setAdoptModal]         = useState(false);
  const [adoptTarget,        setAdoptTarget]        = useState(null);
  const [adoptSubmitting,    setAdoptSubmitting]    = useState(false);

  // Deep-link support: ?module=peer-consult/appointments/... switches the
  // active sidebar entry when the user arrives via a notification link.
  useEffect(() => {
    const m = searchParams.get("module");
    const validKeys = ["dashboard","patients","diagnoses","prescriptions","peer-consult","appointments","history-lookup","adopted-patients","usage","support","profile"];
    if (m && validKeys.includes(m)) {
      setSelectedMenu(m);
    }
  }, [searchParams]);

  // ── data fetchers ─────────────────────────────────────────────────────────────
  const fetchEnrollments = useCallback(async () => {
    // Include inactive patients so the roster stays complete — they're
    // rendered with a gray "Inactive" tag and prescribe is disabled for them.
    const res = await getPatientEnrollments({ include_inactive: true });
    const rows = toArray(res.data);
    setEnrollments(rows.map(r => ({ ...r, key: r.id })));
    return rows;
  }, []);

  const fetchPrescriptions = useCallback(async () => {
    const res = await getPrescriptions();
    const rows = toArray(res.data);
    setPrescriptions(rows.map(r => ({ ...r, key: r.id })));
    return rows;
  }, []);

  const fetchBilling = useCallback(async () => {
    const res = await getBillingRecords();
    const rows = toArray(res.data);
    setBillingRows(rows.map(r => ({ ...r, key: r.id })));
    return rows;
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true); setError(null);
      try {
        const [enrRes, rxRes, billRes, profRes, ppRes] = await Promise.all([
          getPatientEnrollments({ include_inactive: true }),
          getPrescriptions(), getBillingRecords(),
          getDoctorProfile(), getPatientProfiles(),
        ]);
        if (cancelled) return;
        const enr  = toArray(enrRes.data);
        const rx   = toArray(rxRes.data);
        const bill = toArray(billRes.data);
        const prof = Array.isArray(profRes.data)
          ? profRes.data[0]
          : (profRes.data?.results?.[0] ?? profRes.data ?? null);
        const pp   = toArray(ppRes.data);
        const emailMap = {};
        pp.forEach(p => { emailMap[p.id] = p.user_email; });
        setEnrollments(enr.map(r => ({ ...r, key: r.id })));
        setPrescriptions(rx.map(r => ({ ...r, key: r.id })));
        setBillingRows(bill.map(r => ({ ...r, key: r.id })));
        setDoctorProfile(prof);
        setPatientProfiles(pp);
        setProfileEmailMap(emailMap);
        setStats({
          totalPatients:       enr.length,
          activeEnrollments:   enr.filter(e => e.is_active).length,
          prescriptionsIssued: rx.length,
          billingRecords:      bill.length,
        });
      } catch {
        if (!cancelled) setError("Failed to load dashboard data. Please refresh.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [retryCount]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lazy-load usage billing data when navigating to that section
  useEffect(() => {
    if (selectedMenu !== "usage" || usageLoaded) return;
    let cancelled = false;
    async function loadUsage() {
      setUsageLoading(true);
      try {
        const [invRes, sumRes] = await Promise.all([
          getMonthlyInvoices(),
          getUsageSummary(),
        ]);
        if (cancelled) return;
        const invoices = invRes.data?.results ?? (Array.isArray(invRes.data) ? invRes.data : []);
        setUsageInvoices(invoices.map(r => ({ ...r, key: r.id })));
        const sumArr = Array.isArray(sumRes.data) ? sumRes.data : (sumRes.data ? [sumRes.data] : []);
        setUsageSummary(
          sumArr.find(s => s.provider_email === user.email || s.email === user.email)
            ?? sumArr[0] ?? null
        );
        setUsageLoaded(true);
      } catch {
        // silently fail — usage billing is non-critical
      } finally {
        if (!cancelled) setUsageLoading(false);
      }
    }
    loadUsage();
    return () => { cancelled = true; };
  }, [selectedMenu, usageLoaded]); // eslint-disable-line react-hooks/exhaustive-deps


  // ── handlers ──────────────────────────────────────────────────────────────────

  const handleRegister = async (values) => {
    setSubmitting(true);
    try {
      const photoFile = values.id_photo?.[0]?.originFileObj;
      let requestData;
      if (photoFile) {
        requestData = new FormData();
        requestData.append("email",                values.email);
        requestData.append("first_name",           values.first_name);
        requestData.append("last_name",            values.last_name);
        requestData.append("phone",                values.phone || "");
        requestData.append("password",             values.password);
        requestData.append("government_id_number", values.government_id_number);
        requestData.append("government_id_type",   values.government_id_type);
        requestData.append("government_id_photo",  photoFile);
      } else {
        requestData = {
          email:                values.email,
          first_name:           values.first_name,
          last_name:            values.last_name,
          phone:                values.phone || "",
          password:             values.password,
          government_id_number: values.government_id_number,
          government_id_type:   values.government_id_type,
        };
      }
      const res = await registerPatient(requestData);
      setRegModal(false);
      regForm.resetFields();
      const idVerified = res.data.government_id_verified === true;
      Modal.success({
        title:  "Patient Registered Successfully",
        width:  460,
        content: (
          <div>
            <p style={{ margin: "0 0 8px" }}>
              The patient has been registered and enrolled under your care.
            </p>
            <p style={{ margin: "0 0 12px" }}>
              <strong>Email:</strong> {res.data.email}
            </p>
            {idVerified && (
              <div style={{
                background: "#e6f4ff", border: "1px solid #91caff",
                borderRadius: 6, padding: "8px 12px", marginBottom: 12,
              }}>
                <Text style={{ color: "#0958d9", fontSize: 13 }}>
                  <CheckOutlined style={{ marginRight: 6 }} />
                  Government ID automatically verified — enrolled by licensed doctor.
                </Text>
              </div>
            )}
            <div style={{
              background: "#f6ffed", border: "1px solid #b7eb8f",
              borderRadius: 6, padding: "12px 16px",
            }}>
              <p style={{ margin: 0, fontWeight: 600, color: "#237804" }}>
                Patient PIN{idVerified ? " — ID Verified ✓" : " (Government ID)"}
              </p>
              <p style={{
                margin: "6px 0 0", fontSize: 24, letterSpacing: 4,
                fontFamily: "monospace", color: "#389e0d", fontWeight: 700,
                wordBreak: "break-all",
              }}>
                {res.data.pin}
              </p>
            </div>
            <p style={{ color: "#8c8c8c", fontSize: 12, margin: "10px 0 0" }}>
              {idVerified
                ? `Patient enrolled and ID automatically verified. Their PIN is: ${res.data.pin}`
                : "Patient enrolled successfully. Please upload their ID for verification. Provide this PIN for system access — it will not be shown again."}
            </p>
          </div>
        ),
      });
      const rows = await fetchEnrollments();
      setStats(s => ({ ...s, totalPatients: rows.length, activeEnrollments: rows.filter(e => e.is_active).length }));
    } catch (err) {
      const d = err?.response?.data;
      handleApiError(err, d?.government_id_number?.[0] ?? d?.email?.[0] ?? d?.detail ?? "Failed to register patient.");
    } finally { setSubmitting(false); }
  };

  const handleEnroll = async (values) => {
    setSubmitting(true);
    try {
      await enrollPatient({ patient: values.patientEmail, is_active: true });
      message.success("Patient enrolled successfully.");
      setEnrollModal(false); enrollForm.resetFields();
      const rows = await fetchEnrollments();
      setStats(s => ({ ...s, totalPatients: rows.length, activeEnrollments: rows.filter(e => e.is_active).length }));
    } catch (err) {
      const d = err?.response?.data;
      const patientErr = d?.patient?.[0];
      if (patientErr) {
        enrollForm.setFields([{ name: "patientEmail", errors: [patientErr] }]);
      } else {
        handleApiError(err, d?.detail ?? "Failed to enroll patient.");
      }
    } finally { setSubmitting(false); }
  };

  const handlePrescription = async (values) => {
    setSubmitting(true);
    try {
      const meds = values.medications || [];
      const results = await Promise.allSettled(
        meds.map(med => {
          const rawName = (med.medication_name ?? "").trim();
          const medication_name = rawName.includes("{") || rawName.match(/^\d/)
            ? extractCleanDrugName(rawName)
            : rawName;
          const prescriptionData = {
            patient:         values.patient,
            pharmacy:        values.pharmacy,
            medication_name,
            rxnorm_code:     med.rxnorm_code  || null,
            dosage:          med.dosage,
            frequency:       med.frequency    || "",
            duration:        med.duration     || "",
            instructions:    med.instructions || "",
          };
          console.log('Prescription data being sent:', JSON.stringify(prescriptionData, null, 2));
          return createPrescription(prescriptionData);
        })
      );
      const succeeded = results.filter(r => r.status === "fulfilled").length;
      const failed    = results.length - succeeded;

      // If any rejection was caused by an inactive patient, surface the
      // friendly Patient Inactive notification instead of the generic toast.
      const firstRejection = results.find(r => r.status === "rejected");
      const inactiveSurfaced = firstRejection
        ? handleApiError(firstRejection.reason, null)
        : false;

      if (succeeded === results.length) {
        message.success(`${succeeded} prescription${succeeded !== 1 ? "s" : ""} issued successfully.`);
      } else if (succeeded > 0) {
        if (!inactiveSurfaced) {
          message.warning(`${succeeded} of ${results.length} prescriptions issued. ${failed} failed.`);
        }
      } else {
        if (!inactiveSurfaced) {
          message.error("All prescriptions failed to issue. Please check the form and try again.");
        }
        return;
      }
      setRxModal(false);
      rxForm.resetFields();
      const rows = await fetchPrescriptions();
      setStats(s => ({ ...s, prescriptionsIssued: rows.length }));
    } catch (err) {
      const d = err?.response?.data;
      handleApiError(err, d?.detail ?? "Failed to create prescription.");
    } finally { setSubmitting(false); }
  };

  const handleBilling = async (values) => {
    setSubmitting(true);
    try {
      const codes = values.service_codes.split(",").map(c => c.trim()).filter(Boolean);
      await createBillingRecord({
        patient:       values.patient,
        service_date:  values.service_date.format("YYYY-MM-DD"),
        service_codes: codes,
        payor_type:    values.payor_type,
        payor_name:    values.payor_name ?? "",
        amount_billed: parseFloat(values.amount_billed),
      });
      message.success("Billing record submitted.");
      setBillingModal(false); billingForm.resetFields();
      const rows = await fetchBilling();
      setStats(s => ({ ...s, billingRecords: rows.length }));
    } catch (err) {
      const d = err?.response?.data;
      handleApiError(err, d?.service_codes?.[0] ?? d?.amount_billed?.[0] ?? d?.detail ?? "Failed to submit billing.");
    } finally { setSubmitting(false); }
  };

  // ── diagnosis handlers ────────────────────────────────────────────────────────

  const loadDiagnoses = useCallback(async (patientProfileId) => {
    setDiagnosesLoading(true);
    try {
      const params = patientProfileId ? { patient: patientProfileId } : {};
      const res = await getDiagnoses(params);
      const rows = toArray(res.data);
      setDiagnoses(rows.map(r => ({ ...r, key: r.id })));
    } catch {
      message.error("Failed to load diagnoses.");
    } finally {
      setDiagnosesLoading(false);
    }
  }, []);

  const handleIcd10Search = useCallback((value) => {
    if (icd10Timer.current) clearTimeout(icd10Timer.current);
    if (!value || value.length < 2) { setIcd10Options([]); return; }
    icd10Timer.current = setTimeout(async () => {
      setIcd10Searching(true);
      try {
        const url = `https://clinicaltables.nlm.nih.gov/api/icd10cm/v3/search?sf=code,name&terms=${encodeURIComponent(value)}`;
        const resp = await fetch(url);
        const data = await resp.json();
        const items = data[3] ?? [];
        setIcd10Options(items.slice(0, 20).map(([code, name]) => ({
          value: `${code} — ${name}`,
          label: `${code} — ${name}`,
          code,
          name,
        })));
      } catch {
        setIcd10Options([]);
      } finally {
        setIcd10Searching(false);
      }
    }, 400);
  }, []);

  const handleIcd10Select = useCallback((value, option) => {
    dxForm.setFieldsValue({ icd10_code: option.code, icd10_description: option.name });
  }, [dxForm]);

  const handleCreateDiagnosis = async (values) => {
    setSubmitting(true);
    try {
      const payload = {
        ...(editingDiagnosis ? {} : { patient: values.patient }),
        icd10_code:        values.icd10_code,
        icd10_description: values.icd10_description,
        diagnosis_date:    values.diagnosis_date.format("YYYY-MM-DD"),
        severity:          values.severity,
        symptoms:          values.symptoms,
        treatment_plan:    values.treatment_plan ?? "",
        notes:             values.notes ?? "",
        follow_up_date:    values.follow_up_date ? values.follow_up_date.format("YYYY-MM-DD") : null,
      };
      if (editingDiagnosis) {
        await updateDiagnosis(editingDiagnosis.id, payload);
        message.success("Diagnosis updated.");
      } else {
        await createDiagnosis(payload);
        message.success("Diagnosis recorded.");
      }
      setDiagnosisModal(false);
      setEditingDiagnosis(null);
      dxForm.resetFields();
      setIcd10Options([]);
      await loadDiagnoses(selectedPatientProfileId);
    } catch (err) {
      const d = err?.response?.data;
      handleApiError(err, d?.detail ?? d?.non_field_errors?.[0] ?? "Failed to save diagnosis.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkResolved = async (record) => {
    try {
      await updateDiagnosis(record.id, { is_active: false });
      message.success("Diagnosis marked as resolved.");
      await loadDiagnoses(selectedPatientProfileId);
    } catch {
      message.error("Failed to update diagnosis.");
    }
  };

  const handleViewHistory = async (patientProfileId) => {
    setHistoryModal(true);
    setHistoryLoading(true);
    setHistoryData(null);
    try {
      const res = await getPatientHistory(patientProfileId);
      setHistoryData(res.data);
    } catch {
      message.error("Failed to load patient history.");
      setHistoryModal(false);
    } finally {
      setHistoryLoading(false);
    }
  };

  const openDxModal = (record = null) => {
    setEditingDiagnosis(record);
    setIcd10Options([]);
    if (record) {
      dxForm.setFieldsValue({
        patient:           record.patient,
        icd10_code:        record.icd10_code,
        icd10_description: record.icd10_description,
        severity:          record.severity,
        symptoms:          record.symptoms,
        treatment_plan:    record.treatment_plan,
        notes:             record.notes,
      });
    } else {
      dxForm.resetFields();
      if (selectedPatientProfileId) {
        dxForm.setFieldValue("patient", selectedPatientProfileId);
      }
    }
    setDiagnosisModal(true);
  };

  const handleLogout = () => { logout(); navigate("/login"); };
  const userMenuItems = [{ key: "logout", icon: <LogoutOutlined />, label: "Logout", danger: true, onClick: handleLogout }];

  // ── support handlers ──────────────────────────────────────────────────────────
  const loadSupport = useCallback(async () => {
    setSupportLoading(true);
    try {
      const res = await getMyTickets();
      setMyTickets(toArray(res.data).map(r=>({...r,key:r.id})));
      setSupportLoaded(true);
    } catch { /* silent */ } finally { setSupportLoading(false); }
  }, []);

  useEffect(() => {
    if (selectedMenu === "support" && !supportLoaded) loadSupport();
  }, [selectedMenu, supportLoaded, loadSupport]);

  const loadAdoptedPatients = useCallback(async () => {
    setAdoptedLoading(true);
    try {
      const [adoptRes, sharedRes] = await Promise.all([
        getAdoptedPatients(),
        getSharedWithMe().catch(() => ({ data: [] })),
      ]);
      setAdoptedPatients(toArray(adoptRes.data).map(r => ({ ...r, key: r.adoption_id ?? r.patient_profile_id })));
      setSharedWithMe(toArray(sharedRes.data).map(r => ({ ...r, key: r.consent_id ?? r.patient_profile_id })));
      setAdoptedLoaded(true);
    } catch { /* silent */ } finally { setAdoptedLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedMenu === "adopted-patients" && !adoptedLoaded) loadAdoptedPatients();
  }, [selectedMenu, adoptedLoaded, loadAdoptedPatients]);

  const handleAdoptPatient = async (record) => {
    setAdoptSubmitting(true);
    try {
      await adoptPatient(record.patient_profile_id);
      message.success("Patient adopted successfully.");
      setAdoptModal(false);
      setAdoptTarget(null);
      setAdoptedLoaded(false);
      loadAdoptedPatients();
    } catch (err) {
      message.error(err?.response?.data?.detail ?? "Failed to adopt patient.");
    } finally { setAdoptSubmitting(false); }
  };

  const handleCreateTicket = async (values) => {
    try {
      await createTicket(values);
      message.success("Ticket submitted successfully.");
      setTicketModal(false);
      ticketForm.resetFields();
      loadSupport();
    } catch (err) {
      message.error(err?.response?.data?.detail ?? "Failed to submit ticket.");
    }
  };

  // Enrolled-patient options for billing modal (uses User UUID from enrollment)
  const enrolledOpts = enrollments.map(e => (
    <Option key={e.patient} value={e.patient}>{e.patient_email ?? e.patient}</Option>
  ));

  // PatientProfile options for prescription modal — enrolled AND active only.
  // Inactive patients (User.is_active=false) are excluded so the doctor cannot
  // even select them; backend will also reject such requests.
  const enrolledEmailSet = new Set(
    enrollments
      .filter(e => e.is_active && e.patient_is_active !== false)
      .map(e => e.patient_email)
      .filter(Boolean)
  );
  const patientProfileOpts = patientProfiles
    .filter(p => p.is_active !== false && enrolledEmailSet.has(p.user_email))
    .map(p => (
      <Option key={p.id} value={p.id}>{p.user_email ?? p.id}</Option>
    ));

  const openRxModal = async (preselectPatientId = null) => {
    setRxModal(true);
    setProfilesLoading(true);
    try {
      const [ppRes, phRes] = await Promise.all([
        getPatientProfiles(),
        getPharmacies(),
      ]);
      const pp = toArray(ppRes.data);
      setPatientProfiles(pp);
      setPharmacies(toArray(phRes.data));
      if (preselectPatientId) {
        const target = pp.find(p => String(p.id) === String(preselectPatientId));
        if (target && target.is_active !== false) {
          rxForm.setFieldValue("patient", target.id);
        } else if (target && target.is_active === false) {
          showInactivePatientNotice();
        }
      }
    } catch {
      message.error("Could not load patient profiles.");
    } finally {
      setProfilesLoading(false);
    }
  };

  const handleIssueRxForPatient = (patientId) => {
    if (!patientId) return;
    openRxModal(patientId);
  };

  const handlePreferredPharmacy = () => {
    const selectedPatientId = rxForm.getFieldValue("patient");
    if (!selectedPatientId) {
      message.warning("Select a patient first.");
      return;
    }
    const profile = patientProfiles.find(p => p.id === selectedPatientId);
    const preferredId = profile?.preferred_pharmacy;
    if (preferredId && pharmacies.some(ph => String(ph.id) === String(preferredId))) {
      rxForm.setFieldValue("pharmacy", preferredId);
      const ph = pharmacies.find(p => String(p.id) === String(preferredId));
      message.success(`Patient's preferred pharmacy selected: ${ph?.pharmacy_name ?? preferredId}`);
    } else {
      message.info("Patient has no preferred pharmacy set.");
    }
  };

  const rxCols = makeRxCols(profileEmailMap, doctorProfile);
  const profileByEmail = {};
  patientProfiles.forEach(p => { profileByEmail[p.user_email] = p; });
  const enrollmentCols = makeEnrollmentCols(handleViewHistory, handleIssueRxForPatient, profileByEmail);

  const dxCols = [
    { title: "ICD-10",      dataIndex: "icd10_code",        key: "icd10_code",        width: 100 },
    { title: "Condition",   dataIndex: "icd10_description", key: "icd10_description", ellipsis: true },
    { title: "Date",        dataIndex: "diagnosis_date",    key: "diagnosis_date",    width: 110, render: fmtDate },
    { title: "Severity",    dataIndex: "severity",          key: "severity",          width: 100,
      render: s => s ? <Tag color={SEVERITY_COLOR[s] ?? "default"}>{s}</Tag> : "—" },
    { title: "Status",      dataIndex: "is_active",         key: "is_active",         width: 100,
      render: v => v ? <Tag color="blue">Active</Tag> : <Tag color="default">Resolved</Tag> },
    { title: "Follow-up",   dataIndex: "follow_up_date",    key: "follow_up_date",    width: 110, render: fmtDate },
    { title: "Actions",     key: "actions",                 width: 160,
      render: (_, r) => (
        <Row gutter={4} wrap={false}>
          <Col><Button size="small" icon={<EditOutlined />} onClick={() => openDxModal(r)}>Edit</Button></Col>
          {r.is_active && (
            <Col>
              <Button size="small" icon={<CheckOutlined />}
                onClick={() => handleMarkResolved(r)}>
                Resolve
              </Button>
            </Col>
          )}
        </Row>
      )},
  ];

  const statCards = [
    { title: "Total Patients",       value: stats.totalPatients,       color: "#1677ff" },
    { title: "Active Enrollments",   value: stats.activeEnrollments,   color: "#52c41a" },
    { title: "Prescriptions Issued", value: stats.prescriptionsIssued, color: "#722ed1" },
    { title: "Billing Records",      value: stats.billingRecords,      color: "#fa8c16" },
  ];

  // ── views ──────────────────────────────────────────────────────────────────────
  function renderContent() {
    switch (selectedMenu) {

      case "dashboard":
        return (
          <>
            <Title level={4} style={{ marginBottom: 24, color: "#262626" }}>Doctor Dashboard</Title>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              {statCards.map(({ title, value, color }) => (
                <Col xs={24} sm={12} lg={6} key={title}>
                  <Card variant="borderless" style={{ borderRadius: 8 }}>
                    <Statistic title={<Text style={{ color: "#8c8c8c", fontSize: 13 }}>{title}</Text>}
                      value={value} styles={{ content: { color, fontSize: 28, fontWeight: 600 } }} />
                  </Card>
                </Col>
              ))}
            </Row>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24}>
                <Card title={<Text strong>My Patients</Text>} variant="borderless" style={{ borderRadius: 8 }}>
                  <Table columns={enrollmentCols} dataSource={enrollments}
                    pagination={{ pageSize: 6, hideOnSinglePage: true }}
                    locale={{ emptyText: "You have no enrolled patients yet. Use 'Enroll New Patient' to add your first patient." }} size="small" />
                </Card>
              </Col>
            </Row>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24}>
                <Card title={<Text strong>Recent Prescriptions</Text>} variant="borderless" style={{ borderRadius: 8 }}>
                  <Table columns={rxCols} dataSource={prescriptions}
                    pagination={{ pageSize: 6, hideOnSinglePage: true }}
                    locale={{ emptyText: "No prescriptions issued yet. Use the 'Issue Prescription' button to write a prescription for a patient." }} size="small" />
                </Card>
              </Col>
            </Row>
            <Row gutter={[16, 16]}>
              <Col xs={24}>
                <Card title={<Text strong>Quick Actions</Text>} variant="borderless" style={{ borderRadius: 8 }}>
                  <Row gutter={12} wrap>
                    <Col><Button type="primary" icon={<UserAddOutlined />} onClick={() => setRegModal(true)}>Register New Patient</Button></Col>
                    <Col><Button icon={<PlusOutlined />} onClick={() => setEnrollModal(true)}>Enroll Existing Patient</Button></Col>
                    <Col><Button icon={<MedicineBoxOutlined />} onClick={() => openRxModal()}>Issue Prescription</Button></Col>
                    {hasModule("BILLING") && (
                      <Col><Button icon={<DollarOutlined />} onClick={() => setBillingModal(true)}>Submit Billing</Button></Col>
                    )}
                  </Row>
                </Card>
              </Col>
            </Row>
          </>
        );

      case "patients":
        return (
          <>
            <Title level={4} style={{ marginBottom: 24, color: "#262626" }}>My Patients</Title>
            <Card variant="borderless" style={{ borderRadius: 8 }} title={<Text strong>Enrolled Patients</Text>}
              extra={
                <Row gutter={8}>
                  <Col><Button size="small" icon={<UserAddOutlined />} type="primary" onClick={() => setRegModal(true)}>Register New</Button></Col>
                  <Col><Button size="small" icon={<PlusOutlined />} onClick={() => setEnrollModal(true)}>Enroll Existing</Button></Col>
                </Row>
              }>
              <Table columns={enrollmentCols} dataSource={enrollments}
                pagination={{ pageSize: 15, hideOnSinglePage: true }}
                locale={{ emptyText: "You have no enrolled patients yet. Use 'Enroll New Patient' to add your first patient." }} size="small" />
            </Card>
          </>
        );

      case "diagnoses": {
        const selectedProfile = patientProfiles.find(p => p.id === selectedPatientProfileId);
        const activeDxCount   = diagnoses.filter(d => d.is_active).length;
        const lastDxDate      = diagnoses[0]?.diagnosis_date ?? null;

        // Build combined timeline items from diagnoses + prescriptions for selected patient
        const timelineItems = [
          ...diagnoses.map(d => ({
            date: d.diagnosis_date, type: "diagnosis",
            label: `[Diagnosis] ${d.icd10_code} — ${d.icd10_description}`,
          })),
          ...prescriptions
            .filter(p => !selectedPatientProfileId || p.patient === selectedPatientProfileId)
            .map(p => ({
              date: p.created_at?.slice(0, 10), type: "prescription",
              label: `[Rx] ${p.medication_name ?? "—"} · ${p.dosage ?? "—"}`,
            })),
        ].sort((a, b) => (b.date ?? "").localeCompare(a.date ?? ""));

        return (
          <>
            <Title level={4} style={{ marginBottom: 16, color: "#262626" }}>Diagnoses</Title>

            {/* Patient selector */}
            <Card variant="borderless" style={{ borderRadius: 8, marginBottom: 16 }}>
              <Row gutter={16} align="middle">
                <Col xs={24} sm={10}>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select a patient to view their diagnoses"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    value={selectedPatientProfileId}
                    onChange={async (val) => {
                      setSelectedPatientProfileId(val ?? null);
                      await loadDiagnoses(val ?? null);
                    }}
                  >
                    {patientProfiles.map(p => (
                      <Option key={p.id} value={p.id}>{p.user_email ?? p.id}</Option>
                    ))}
                  </Select>
                </Col>
                <Col>
                  <Button type="primary" icon={<PlusOutlined />}
                    onClick={() => openDxModal()}>
                    Add Diagnosis
                  </Button>
                </Col>
              </Row>
            </Card>

            {/* Patient summary card */}
            {selectedProfile && (
              <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={24} sm={8}>
                  <Card variant="borderless" style={{ borderRadius: 8 }}>
                    <Statistic title={<Text style={{ color: "#8c8c8c", fontSize: 13 }}>Total Diagnoses</Text>}
                      value={diagnoses.length} styles={{ content: { color: "#1677ff", fontSize: 24, fontWeight: 600 } }} />
                  </Card>
                </Col>
                <Col xs={24} sm={8}>
                  <Card variant="borderless" style={{ borderRadius: 8 }}>
                    <Statistic title={<Text style={{ color: "#8c8c8c", fontSize: 13 }}>Active Conditions</Text>}
                      value={activeDxCount} styles={{ content: { color: "#f5222d", fontSize: 24, fontWeight: 600 } }} />
                  </Card>
                </Col>
                <Col xs={24} sm={8}>
                  <Card variant="borderless" style={{ borderRadius: 8 }}>
                    <Statistic title={<Text style={{ color: "#8c8c8c", fontSize: 13 }}>Last Diagnosis</Text>}
                      value={lastDxDate ? new Date(lastDxDate).toLocaleDateString() : "—"}
                      styles={{ content: { color: "#52c41a", fontSize: 20, fontWeight: 600 } }} />
                  </Card>
                </Col>
              </Row>
            )}

            {/* Diagnoses table */}
            <Card variant="borderless" style={{ borderRadius: 8, marginBottom: 16 }}
              title={<Text strong>Diagnoses</Text>}>
              <Spin spinning={diagnosesLoading}>
                <Table columns={dxCols} dataSource={diagnoses}
                  pagination={{ pageSize: 15, hideOnSinglePage: true }}
                  locale={{ emptyText: selectedPatientProfileId ? "No diagnoses recorded for this patient yet. Click 'Add Diagnosis' to record their first diagnosis." : "Select a patient above to view and manage their diagnoses." }}
                  size="small" />
              </Spin>
            </Card>

            {/* Medical timeline */}
            {selectedPatientProfileId && timelineItems.length > 0 && (
              <Card variant="borderless" style={{ borderRadius: 8 }}
                title={<Text strong>Medical Timeline</Text>}>
                <Timeline
                  items={timelineItems.map(item => ({
                    color: item.type === "diagnosis" ? "red" : "blue",
                    children: (
                      <span>
                        <Text type="secondary" style={{ fontSize: 12 }}>{item.date ?? "—"}</Text>
                        <br />
                        <Text>{item.label}</Text>
                      </span>
                    ),
                  }))}
                />
              </Card>
            )}
          </>
        );
      }

      case "prescriptions":
        return (
          <>
            <Title level={4} style={{ marginBottom: 24, color: "#262626" }}>Prescriptions</Title>
            <Card variant="borderless" style={{ borderRadius: 8 }} title={<Text strong>Issued Prescriptions</Text>}
              extra={
                <Row gutter={8}>
                  <Col>
                    <Button size="small" icon={<PrinterOutlined />}
                      onClick={() => printAllPrescriptions(prescriptions, doctorProfile)}
                      disabled={!prescriptions.length}>
                      Print All
                    </Button>
                  </Col>
                  <Col>
                    <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => openRxModal()}>
                      Issue Prescription
                    </Button>
                  </Col>
                </Row>
              }>
              <Table columns={rxCols} dataSource={prescriptions}
                pagination={{ pageSize: 15, hideOnSinglePage: true }}
                locale={{ emptyText: "No prescriptions issued yet. Use the 'Issue Prescription' button to write a prescription for a patient." }} size="small" />
            </Card>
          </>
        );

      case "peer-consult":
        return <PeerConsultation doctorProfile={doctorProfile} />;

      case "appointments":
        return <Appointments doctorProfile={doctorProfile} />;

      case "history-lookup":
        return <PatientLookup onViewHistory={handleViewHistory} />;

      case "usage": {
        const now = new Date();
        const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
        const currentInvoice = usageInvoices.find(inv => inv.billing_month === currentMonth) ?? null;
        const INVOICE_STATUS_COLOR = { DRAFT: "default", SENT: "blue", PAID: "green", OVERDUE: "red" };
        const invoiceColumns = [
          { title: "Month",         dataIndex: "billing_month",   key: "billing_month",   width: 150,
            render: m => formatMonth(m) },
          { title: "Registrations", key: "registrations",
            render: (_, r) => r.total_patient_registrations ?? 0 },
          { title: "Prescriptions", key: "prescriptions",
            render: (_, r) => r.total_prescriptions_issued ?? 0 },
          { title: "Total Amount",  dataIndex: "total_amount",    key: "total_amount",    width: 130,
            render: a => a != null ? `$${parseFloat(a).toFixed(2)}` : "—" },
          { title: "Status",        dataIndex: "status",          key: "status",          width: 100,
            render: s => <Tag color={INVOICE_STATUS_COLOR[s] ?? "default"}>{s ?? "—"}</Tag> },
          { title: "Actions",       key: "actions",               width: 110,
            render: (_, r) => (
              <Button size="small"
                onClick={() => { setInvoiceDetailTarget(r); setInvoiceDetailOpen(true); }}>
                View Details
              </Button>
            )},
        ];
        return (
          <Spin spinning={usageLoading} description="Loading…">
            <Title level={4} style={{ marginBottom: 24, color: "#262626" }}>Usage &amp; Invoices</Title>
            {/* Current Month Summary */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={8}>
                <Card variant="borderless" style={{ borderRadius: 8 }}>
                  <Statistic
                    title={<Text style={{ color: "#8c8c8c", fontSize: 13 }}>Patients Registered (This Month)</Text>}
                    value={usageSummary?.registrations ?? currentInvoice?.total_patient_registrations ?? 0}
                    styles={{ content: { color: "#1677ff", fontSize: 28, fontWeight: 600 } }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card variant="borderless" style={{ borderRadius: 8 }}>
                  <Statistic
                    title={<Text style={{ color: "#8c8c8c", fontSize: 13 }}>Prescriptions Issued (This Month)</Text>}
                    value={usageSummary?.prescriptions_issued ?? currentInvoice?.total_prescriptions_issued ?? 0}
                    styles={{ content: { color: "#52c41a", fontSize: 28, fontWeight: 600 } }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card variant="borderless" style={{ borderRadius: 8 }}>
                  <Statistic
                    title={<Text style={{ color: "#8c8c8c", fontSize: 13 }}>Estimated Fee (This Month)</Text>}
                    value={usageSummary?.estimated_fee != null
                      ? parseFloat(usageSummary.estimated_fee).toFixed(2)
                      : (currentInvoice?.total_amount != null ? parseFloat(currentInvoice.total_amount).toFixed(2) : "0.00")}
                    prefix="$"
                    styles={{ content: { color: "#fa8c16", fontSize: 28, fontWeight: 600 } }}
                  />
                </Card>
              </Col>
            </Row>
            {/* Invoice History */}
            <Card variant="borderless" style={{ borderRadius: 8 }} title={<Text strong>Invoice History</Text>}>
              <Table
                columns={invoiceColumns}
                dataSource={usageInvoices}
                pagination={{ pageSize: 12, hideOnSinglePage: true }}
                locale={{ emptyText: "No invoices generated yet" }}
                size="small"
              />
            </Card>
          </Spin>
        );
      }

      case "profile":
        return (
          <>
            <Title level={4} style={{ marginBottom: 24, color: "#262626" }}>My Profile</Title>
            <Card variant="borderless" style={{ borderRadius: 8 }}>
              {doctorProfile ? (
                <Descriptions column={2} bordered size="small">
                  <Descriptions.Item label="Email">{doctorProfile.user_email ?? user.email}</Descriptions.Item>
                  <Descriptions.Item label="Specialty">{doctorProfile.specialty ?? "—"}</Descriptions.Item>
                  <Descriptions.Item label="License No.">{doctorProfile.license_number ?? "—"}</Descriptions.Item>
                  <Descriptions.Item label="Experience">
                    {doctorProfile.years_of_experience != null ? `${doctorProfile.years_of_experience} years` : "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Verified" span={2}>
                    {doctorProfile.is_verified
                      ? <Tag color="green">Verified</Tag>
                      : <Tag color="orange">Pending Verification</Tag>}
                  </Descriptions.Item>
                  <Descriptions.Item label="Bio" span={2}>{doctorProfile.bio || "—"}</Descriptions.Item>
                </Descriptions>
              ) : (
                <Text type="secondary">No profile found. Please contact your administrator.</Text>
              )}
            </Card>
            <ChangePasswordForm />
          </>
        );

      case "support":
        return (
          <Spin spinning={supportLoading}>
            <Title level={4} style={{ marginBottom: 16, color: "#262626" }}>Support</Title>
            <Card variant="borderless" style={{ borderRadius: 8 }} title={<Text strong>My Tickets</Text>}
              extra={<Button type="primary" size="small" icon={<PlusOutlined />} onClick={()=>setTicketModal(true)}>Report an Issue</Button>}>
              <Table size="small" dataSource={myTickets} pagination={{ pageSize: 10, hideOnSinglePage: true }}
                locale={{ emptyText: "No tickets yet" }}
                columns={[
                  { title:"ID",       dataIndex:"id",       key:"id",       width:90, render:id=><Text code style={{fontSize:11}}>{String(id).slice(0,8)}</Text> },
                  { title:"Subject",  dataIndex:"subject",  key:"subject",  ellipsis:true },
                  { title:"Priority", dataIndex:"priority", key:"priority", width:100, render:p=><Tag color={{LOW:"default",MEDIUM:"blue",HIGH:"orange",CRITICAL:"red"}[p]??"default"}>{p}</Tag> },
                  { title:"Status",   dataIndex:"status",   key:"status",   width:110, render:s=><Tag color={{OPEN:"blue",IN_PROGRESS:"orange",RESOLVED:"green",CLOSED:"default",ESCALATED:"red"}[s]??"default"}>{s?.replace(/_/g," ")}</Tag> },
                  { title:"Created",  dataIndex:"created_at",key:"created_at",width:110, render:fmtDate },
                  { title:"Actions",  key:"actions",        width:80, render:(_,r)=><Button size="small" onClick={()=>setTicketDetail(r)}>View</Button> },
                ]}
              />
            </Card>
          </Spin>
        );

      case "adopted-patients": {
        const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString() : "—";
        const adoptedCols = [
          { title: "Patient", key: "name", render: (_, r) => <Text strong>{r.first_name} {r.last_name}</Text> },
          { title: "PIN", dataIndex: "pin", key: "pin", width: 120 },
          { title: "Original Org", dataIndex: "original_organisation", key: "original_organisation", render: v => v ?? "—" },
          { title: "Hospital", dataIndex: "adopting_hospital", key: "adopting_hospital", render: v => v ?? "—" },
          { title: "Adopted", dataIndex: "adopted_at", key: "adopted_at", width: 130, render: fmtDate },
          { title: "Last Visit", dataIndex: "last_visit", key: "last_visit", width: 130, render: fmtDate },
          { title: "Actions", key: "actions", width: 110, render: (_, r) => (
            <Button size="small" icon={<HistoryOutlined />} onClick={() => {
              const id = r.patient_profile_id;
              setHistoryLoading(true);
              getPatientHistory(id)
                .then(res => { setHistoryData(res.data); setHistoryModal(true); })
                .catch(() => message.error("Failed to load history."))
                .finally(() => setHistoryLoading(false));
            }}>History</Button>
          )},
        ];
        const sharedCols = [
          { title: "Patient", key: "name", render: (_, r) => <span>{r.first_name} {r.last_name}</span> },
          { title: "Email", dataIndex: "email", key: "email", ellipsis: true },
          { title: "PIN", dataIndex: "pin", key: "pin", width: 120 },
          { title: "Shared At", dataIndex: "shared_at", key: "shared_at", width: 130, render: v => v ? new Date(v).toLocaleDateString() : "—" },
          { title: "Actions", key: "actions", width: 110, render: (_, r) => (
            <Button size="small" type="primary" onClick={() => { setAdoptTarget(r); setAdoptModal(true); }}>
              Adopt
            </Button>
          )},
        ];

        return (
          <Spin spinning={adoptedLoading}>
            <Title level={4} style={{ marginBottom: 16, color: "#262626" }}>Adopted Patients</Title>
            <Alert type="info" showIcon style={{ marginBottom: 16, borderRadius: 8 }}
              message="Cross-Organisation Patients"
              description={`These patients have shared their records with your organisation${organisation ? ` (${organisation})` : ""}. Any prescriptions or diagnoses you add will be billed to your organisation and will appear in the patient's complete medical history attributed to you.`}
            />

            <Card variant="borderless" style={{ borderRadius: 8, marginBottom: 16 }}
              title={<Text strong>Adopted Patients</Text>}>
              <Table columns={adoptedCols} dataSource={adoptedPatients}
                pagination={{ pageSize: 15, hideOnSinglePage: true }}
                locale={{ emptyText: "No adopted patients yet. Adopt patients from the list below who have shared their records with you." }}
                size="small" />
            </Card>

            <Card variant="borderless" style={{ borderRadius: 8 }}
              title={<Text strong>Patients Who Shared With You</Text>}
              extra={<Button size="small" icon={<HistoryOutlined />} onClick={loadAdoptedPatients}>Refresh</Button>}>
              <Table columns={sharedCols} dataSource={sharedWithMe}
                pagination={{ pageSize: 10, hideOnSinglePage: true }}
                locale={{ emptyText: "No patients have shared their records with you yet." }}
                size="small" />
            </Card>

            <Modal
              title="Adopt Patient"
              open={adoptModal}
              onCancel={() => { setAdoptModal(false); setAdoptTarget(null); }}
              onOk={() => adoptTarget && handleAdoptPatient(adoptTarget)}
              okText="Adopt Patient"
              okButtonProps={{ loading: adoptSubmitting }}
            >
              {adoptTarget && (
                <Alert type="info" showIcon
                  message={`Adopt ${adoptTarget.first_name} ${adoptTarget.last_name}?`}
                  description="This patient will appear in your Adopted Patients list. You can view their history and issue prescriptions billed to your organisation."
                />
              )}
            </Modal>
          </Spin>
        );
      }

      default: return null;
    }
  }

  // ── render ────────────────────────────────────────────────────────────────────
  const menuItems = [
    { key: "dashboard",        icon: <DashboardOutlined />,       label: "Overview" },
    { key: "patients",         icon: <TeamOutlined />,            label: "My Patients" },
    { key: "adopted-patients", icon: <GlobalOutlined />,          label: adoptedPatients.length > 0
        ? <span>Adopted Patients <Tag style={{marginLeft:4,fontSize:11}} color="purple">{adoptedPatients.length}</Tag></span>
        : "Adopted Patients" },
    { key: "diagnoses",      icon: <FileTextOutlined />,        label: "Diagnoses" },
    { key: "prescriptions",  icon: <MedicineBoxOutlined />,     label: "Prescriptions" },
    { key: "peer-consult",   icon: <MessageOutlined />,         label: "Peer Consultation" },
    { key: "appointments",   icon: <CalendarOutlined />,        label: "Appointments" },
    { key: "history-lookup", icon: <IdcardOutlined />,          label: "Medical History Lookup" },
    ...(hasModule("BILLING") ? [{ key: "usage", icon: <BarChartOutlined />, label: "Billing" }] : []),
    { key: "support",        icon: <CustomerServiceOutlined />, label: "Support" },
    { key: "profile",        icon: <UserOutlined />,            label: "My Profile" },
  ];

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider collapsible collapsed={collapsed} onCollapse={setCollapsed} width={220}
        style={{ background: "#fff", borderRight: "1px solid #f0f0f0" }}>
        <div style={{
          height: 64, display: "flex", alignItems: "center", justifyContent: "center",
          borderBottom: "1px solid #f0f0f0", padding: "0 16px",
        }}>
          {!collapsed && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.3 }}>
              <Text strong style={{ color: "#1677ff", fontSize: 13, whiteSpace: "nowrap" }}>Digital Healthcare Network</Text>
              <Text style={{ color: "#1677ff", fontSize: 12, whiteSpace: "nowrap" }}>Doctor</Text>
            </div>
          )}
        </div>
        <Menu mode="inline" selectedKeys={[selectedMenu]} items={menuItems}
          onClick={({ key }) => setSelectedMenu(key)} style={{ border: "none", marginTop: 8 }} />
      </Sider>

      <Layout>
        <Header style={{
          background: "#fff", padding: "0 24px", display: "flex", alignItems: "center",
          justifyContent: "space-between", borderBottom: "1px solid #f0f0f0",
          position: "sticky", top: 0, zIndex: 100,
        }}>
          <div>
            <Text strong style={{ fontSize: 16, color: "#1677ff" }}>MCHUSA</Text>
            <Text style={{ fontSize: 14, color: "#8c8c8c", marginLeft: 8 }}>| Digital Healthcare Network</Text>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <NotificationBell />
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" arrow>
              <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                <Avatar size={32} icon={<UserOutlined />} style={{ background: "#1677ff" }} />
                <Text style={{ fontSize: 14, color: "#262626" }}>{user.email ?? "Doctor"}</Text>
              </div>
            </Dropdown>
          </div>
        </Header>

        <Content style={{ padding: 24, background: "#f5f7fa" }}>
          {loading ? (
            <>
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                {[1, 2, 3, 4].map(i => (
                  <Col xs={24} sm={12} lg={6} key={i}>
                    <Card variant="borderless" style={{ borderRadius: 8 }}>
                      <Skeleton active paragraph={{ rows: 1 }} />
                    </Card>
                  </Col>
                ))}
              </Row>
              <Card variant="borderless" style={{ borderRadius: 8, marginBottom: 24 }}>
                <Skeleton active paragraph={{ rows: 5 }} />
              </Card>
              <Card variant="borderless" style={{ borderRadius: 8 }}>
                <Skeleton active paragraph={{ rows: 4 }} />
              </Card>
            </>
          ) : error ? (
            <Alert type="error" message={error} showIcon style={{ marginBottom: 24 }}
              action={<Button size="small" onClick={() => setRetryCount(c => c + 1)}>Retry</Button>}
              onClose={() => setError(null)} closable />
          ) : renderContent()}
        </Content>
      </Layout>

      {/* ── Add / Edit Diagnosis Modal ── */}
      <Modal
        title={editingDiagnosis ? "Edit Diagnosis" : "Add Diagnosis"}
        open={diagnosisModal}
        onOk={() => dxForm.submit()}
        onCancel={() => { setDiagnosisModal(false); setEditingDiagnosis(null); dxForm.resetFields(); setIcd10Options([]); }}
        okText={editingDiagnosis ? "Save Changes" : "Record Diagnosis"}
        confirmLoading={submitting}
        destroyOnHidden
        width={660}

        keyboard={true}
      >
        <Form form={dxForm} layout="vertical" onFinish={handleCreateDiagnosis}>
          {editingDiagnosis ? (
            <Form.Item label="Patient">
              <Input
                disabled
                value={profileEmailMap[editingDiagnosis.patient] ?? String(editingDiagnosis.patient)}
              />
            </Form.Item>
          ) : (
            <Form.Item name="patient" label="Patient" rules={[{ required: true, message: "Select a patient" }]}>
              <Select placeholder="Select patient" showSearch optionFilterProp="children">
                {patientProfiles.map(p => (
                  <Option key={p.id} value={p.id}>{p.user_email ?? p.id}</Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {/* ICD-10 autocomplete */}
          <Form.Item label="ICD-10 Code — Search Condition">
            <AutoComplete
              options={icd10Options}
              onSearch={handleIcd10Search}
              onSelect={handleIcd10Select}
              filterOption={false}
              notFoundContent={
                icd10Searching
                  ? <Spin size="small" />
                  : icd10Options.length === 0
                    ? <Text type="secondary" style={{ fontSize: 12, padding: "4px 8px", display: "block" }}>
                        No ICD-10 codes found. Try different keywords or enter the code manually below.
                      </Text>
                    : null
              }
              placeholder="Type condition name or ICD-10 code (e.g. J06.9)"
            >
              <Input suffix={icd10Searching ? <Spin size="small" /> : null} />
            </AutoComplete>
          </Form.Item>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="icd10_code" label="ICD-10 Code" rules={[{ required: true, message: "Required" }]}>
                <Input placeholder="e.g. J06.9" />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item name="icd10_description" label="Description" rules={[{ required: true, message: "Required" }]}>
                <Input placeholder="e.g. Upper Respiratory Infection" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="diagnosis_date" label="Diagnosis Date" rules={[{ required: true, message: "Required" }]}>
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="severity" label="Severity" rules={[{ required: true, message: "Required" }]}>
                <Select placeholder="Select severity">
                  <Option value="MILD">Mild</Option>
                  <Option value="MODERATE">Moderate</Option>
                  <Option value="SEVERE">Severe</Option>
                  <Option value="CRITICAL">Critical</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="symptoms" label="Symptoms" rules={[{ required: true, message: "Describe symptoms" }]}>
            <Input.TextArea rows={3} placeholder="Describe presenting symptoms" />
          </Form.Item>
          <Form.Item name="treatment_plan" label="Treatment Plan (optional)">
            <Input.TextArea rows={2} placeholder="Medications, procedures, referrals…" />
          </Form.Item>
          <Form.Item name="follow_up_date" label="Follow-up Date (optional)">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="notes" label={<span>Confidential Notes <Tag color="orange" style={{ fontSize: 11 }}>PHI</Tag></span>}>
            <Input.TextArea rows={2} placeholder="Internal notes — not visible to patient or shared doctors" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Patient Full History Modal ── */}
      <Modal
        title={<span><HistoryOutlined style={{ marginRight: 8 }} />Patient Medical History</span>}
        open={historyModal}
        onCancel={() => { setHistoryModal(false); setHistoryData(null); }}
        footer={<Button onClick={() => { setHistoryModal(false); setHistoryData(null); }}>Close</Button>}
        width={720}
        destroyOnHidden

      >
        <Spin spinning={historyLoading}>
          {historyData ? (
            <>
              <Descriptions bordered size="small" column={1} style={{ marginBottom: 16 }}>
                <Descriptions.Item label="Patient Email">{historyData.patient_email ?? "—"}</Descriptions.Item>
              </Descriptions>

              <Card size="small" title={<Text strong>Diagnoses ({historyData.diagnoses?.length ?? 0})</Text>}
                style={{ marginBottom: 12 }}>
                <Table
                  size="small"
                  pagination={false}
                  dataSource={(historyData.diagnoses ?? []).map((d, i) => ({ ...d, key: d.id ?? i }))}
                  columns={[
                    { title: "Date",      dataIndex: "diagnosis_date",    key: "diagnosis_date",    width: 100, render: fmtDate },
                    { title: "ICD-10",    dataIndex: "icd10_code",        key: "icd10_code",        width: 90 },
                    { title: "Condition", dataIndex: "icd10_description", key: "icd10_description", ellipsis: true },
                    { title: "Severity",  dataIndex: "severity",          key: "severity",          width: 90,
                      render: s => s ? <Tag color={SEVERITY_COLOR[s] ?? "default"}>{s}</Tag> : "—" },
                  ]}
                  locale={{ emptyText: "No diagnoses" }}
                />
              </Card>

              <Card size="small" title={<Text strong>Prescriptions ({historyData.prescriptions?.length ?? 0})</Text>}
                style={{ marginBottom: 12 }}>
                <Table
                  size="small"
                  pagination={false}
                  dataSource={(historyData.prescriptions ?? []).map((p, i) => ({ ...p, key: p.id ?? i }))}
                  columns={[
                    { title: "Date",       dataIndex: "created_at",      key: "created_at",      width: 100, render: fmtDate },
                    { title: "Medication", dataIndex: "medication_name", key: "medication_name", ellipsis: true },
                    { title: "Dosage",     dataIndex: "dosage",          key: "dosage",          width: 90 },
                    { title: "Status",     dataIndex: "is_dispensed",    key: "is_dispensed",    width: 100,
                      render: v => v ? <Tag color="green">Dispensed</Tag> : <Tag color="orange">Pending</Tag> },
                  ]}
                  locale={{ emptyText: "No prescriptions" }}
                />
              </Card>

              {hasModule("EMS") && (
                <Card size="small" title={<Text strong>EMS Requests ({historyData.ems_requests?.length ?? 0})</Text>}
                  style={{ marginBottom: 12 }}>
                  <Table
                    size="small"
                    pagination={false}
                    dataSource={(historyData.ems_requests ?? []).map((e, i) => ({ ...e, key: e.id ?? i }))}
                    columns={[
                      { title: "Requested", dataIndex: "requested_at", key: "requested_at", width: 130, render: fmtDate },
                      { title: "Status",    dataIndex: "status",       key: "status",       width: 110,
                        render: s => <Tag color={{ PENDING:"orange", DISPATCHED:"blue", IN_TRANSIT:"purple", COMPLETED:"green", CANCELLED:"default" }[s] ?? "default"}>{s ?? "—"}</Tag> },
                    ]}
                    locale={{ emptyText: "No EMS requests" }}
                  />
                </Card>
              )}

              {hasModule("OPHTHALMOLOGY") && (
                <Card size="small" title={<Text strong>Eye Examinations ({historyData.eye_examinations?.length ?? 0})</Text>}>
                  <Table
                    size="small"
                    pagination={false}
                    dataSource={(historyData.eye_examinations ?? []).map((e, i) => ({ ...e, key: e.id ?? i }))}
                    columns={[
                      { title: "Examination Date", dataIndex: "examination_date", key: "examination_date", width: 160, render: fmtDate },
                    ]}
                    locale={{ emptyText: "No eye examinations" }}
                  />
                </Card>
              )}
            </>
          ) : !historyLoading ? (
            <Text type="secondary">No data available.</Text>
          ) : null}
        </Spin>
      </Modal>

      {/* ── Register New Patient Modal ── */}
      <Modal title="Register New Patient" open={regModal} onOk={() => regForm.submit()}
        onCancel={() => { setRegModal(false); regForm.resetFields(); }}
        okText="Register Patient" confirmLoading={submitting} destroyOnHidden
 keyboard={true} width={520}>
        <Alert
          message="The patient's government ID number will be used as their PIN for system access."
          type="info" showIcon style={{ marginBottom: 16 }} />
        <Form form={regForm} layout="vertical" onFinish={handleRegister}>
          <Form.Item name="email" label="Email"
            rules={[{ required: true, message: "Required" }, { type: "email", message: "Enter a valid email" }]}>
            <Input placeholder="patient@example.com" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="first_name" label="First Name" rules={[{ required: true, message: "Required" }]}>
                <Input placeholder="First name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="last_name" label="Last Name" rules={[{ required: true, message: "Required" }]}>
                <Input placeholder="Last name" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="phone" label="Phone (optional)">
            <Input placeholder="+63 912 345 6789" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={10}>
              <Form.Item name="government_id_type" label="ID Type"
                rules={[{ required: true, message: "Required" }]}>
                <Select placeholder="Select type">
                  <Option value="PASSPORT">Passport</Option>
                  <Option value="DRIVERS_LICENSE">Driver's License</Option>
                  <Option value="NATIONAL_ID">National ID</Option>
                  <Option value="OTHER">Other</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={14}>
              <Form.Item name="government_id_number" label="Government ID Number"
                rules={[{ required: true, message: "Required" }]}
                extra="Used as the patient's PIN for system access.">
                <Input placeholder="Exactly as shown on document" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="id_photo" label="ID Photo (optional)"
            valuePropName="fileList"
            getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList}>
            <Upload beforeUpload={() => false} maxCount={1}
              accept=".jpg,.jpeg,.png,.pdf" listType="picture">
              <Button icon={<UploadOutlined />}>Upload ID Photo</Button>
            </Upload>
          </Form.Item>
          <Form.Item name="password" label="Temporary Password"
            rules={[{ required: true, message: "Required" }, { min: 8, message: "Minimum 8 characters" }]}>
            <Input.Password placeholder="Minimum 8 characters" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Enroll Existing Patient Modal ── */}
      <Modal title="Enroll Existing Patient" open={enrollModal} onOk={() => enrollForm.submit()}
        onCancel={() => { setEnrollModal(false); enrollForm.resetFields(); }}
        okText="Enroll" confirmLoading={submitting} destroyOnHidden
 keyboard={true}>
        <Form form={enrollForm} layout="vertical" onFinish={handleEnroll}>
          <Form.Item name="patientEmail" label="Patient Email, PIN, or ID"
            rules={[{ required: true, message: "Enter the patient's email, PIN, or ID" }]}
            extra="You can search by email address, PIN number, or patient UUID">
            <Input placeholder="Enter patient email, PIN number, or patient ID" />
          </Form.Item>
          {!hasFullAccess && organisation && (
            <Text type="secondary" style={{ display: "block", fontSize: 12, marginTop: -12, marginBottom: 12 }}>
              Only patients in {organisation} can be enrolled.
            </Text>
          )}
          <Form.Item name="notes" label="Notes (optional)">
            <Input.TextArea rows={3} placeholder="Enrollment notes" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Issue Prescription Modal (multi-drug) ── */}
      <Modal title="Issue Prescription" open={rxModal} onOk={() => rxForm.submit()}
        onCancel={() => { setRxModal(false); rxForm.resetFields(); }}
        okText="Issue Prescriptions" confirmLoading={submitting} destroyOnHidden width={700}
 keyboard={true}>
        <Form form={rxForm} layout="vertical" onFinish={handlePrescription}
          initialValues={{ medications: [{}] }}>
          <Form.Item name="patient" label="Patient" rules={[{ required: true, message: "Select a patient" }]}>
            <Select placeholder="Select enrolled patient" showSearch optionFilterProp="children"
              loading={profilesLoading} disabled={profilesLoading}>
              {patientProfileOpts}
            </Select>
          </Form.Item>

          <Form.Item
            label={
              <span>
                {!hasFullAccess && organisation
                  ? `Select Pharmacy (${organisation})`
                  : "Send to Pharmacy"}
                <span style={{ color: "#ff4d4f", marginLeft: 4 }}>*</span>
              </span>
            }>
            {!hasFullAccess && organisation && pharmacies.length === 0 && !profilesLoading ? (
              <Alert
                type="warning"
                showIcon
                message={`No pharmacies registered in ${organisation}. Contact your Operations Manager to add a pharmacy.`}
                style={{ marginBottom: 8 }}
              />
            ) : (
              <Row gutter={8} align="middle">
                <Col flex="auto">
                  <Form.Item name="pharmacy" noStyle
                    rules={[{ required: true, message: "Select a pharmacy to direct this prescription to" }]}>
                    <Select placeholder="Select pharmacy" showSearch optionFilterProp="children"
                      loading={profilesLoading} disabled={profilesLoading} style={{ width: "100%" }}>
                      {pharmacies.map(ph => (
                        <Option key={ph.id} value={ph.id}>
                          {ph.pharmacy_name}{ph.address ? ` — ${ph.address}` : ""}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col>
                  <Button size="small" onClick={handlePreferredPharmacy} title="Auto-select patient's preferred pharmacy">
                    Patient's Preferred
                  </Button>
                </Col>
              </Row>
            )}
            <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 4 }}>
              The prescription will be sent directly to this pharmacy. The patient only needs to pick it up.
            </div>
          </Form.Item>

          <Form.List name="medications">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }, index) => (
                  <MedicationCard
                    key={key}
                    fieldKey={key}
                    name={name}
                    index={index}
                    totalFields={fields.length}
                    restField={restField}
                    rxForm={rxForm}
                    onRemove={() => remove(name)}
                  />
                ))}
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}
                  style={{ marginBottom: 12 }}>
                  + Add Another Medication
                </Button>
              </>
            )}
          </Form.List>

          <Form.Item name="general_notes" label="General Notes (optional)">
            <Input.TextArea rows={2} placeholder="Notes applying to the entire prescription visit" />
          </Form.Item>

          <Alert
            type="info"
            showIcon
            message="The prescription will be sent directly to the selected pharmacy. The patient only needs to present their PIN to collect it."
            style={{ marginTop: 4 }}
          />
        </Form>
      </Modal>

      {/* ── Invoice Detail Modal ── */}
      {(() => {
        const r         = invoiceDetailTarget;
        const rCount    = r?.total_patient_registrations ?? 0;
        const pCount    = r?.total_prescriptions_issued ?? 0;
        const rSubtotal = parseFloat(r?.patient_registration_fee ?? 0);
        const pSubtotal = parseFloat(r?.prescription_issued_fee ?? 0);
        const rFeeUnit  = rCount > 0 ? (rSubtotal / rCount) : 0;
        const pFeeUnit  = pCount > 0 ? (pSubtotal / pCount) : 0;
        const INV_COLOR = { DRAFT: "default", SENT: "blue", PAID: "green", OVERDUE: "red" };
        return (
          <Modal
            title={`Invoice — ${formatMonth(r?.billing_month)}`}
            open={invoiceDetailOpen}
            onCancel={() => { setInvoiceDetailOpen(false); setInvoiceDetailTarget(null); }}
            footer={[
              <Button key="close" onClick={() => { setInvoiceDetailOpen(false); setInvoiceDetailTarget(null); }}>
                Close
              </Button>,
            ]}
            destroyOnHidden>
            <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Billing Month">{formatMonth(r?.billing_month)}</Descriptions.Item>
              <Descriptions.Item label="Patient Registrations">
                {rCount} × ${rFeeUnit.toFixed(2)} = <Text strong>${rSubtotal.toFixed(2)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Prescriptions Issued">
                {pCount} × ${pFeeUnit.toFixed(2)} = <Text strong>${pSubtotal.toFixed(2)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Total Amount Due">
                <Text strong style={{ fontSize: 15 }}>${parseFloat(r?.total_amount ?? 0).toFixed(2)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={INV_COLOR[r?.status] ?? "default"}>{r?.status ?? "—"}</Tag>
              </Descriptions.Item>
            </Descriptions>
            <Alert
              type="info"
              showIcon
              message="Payment Instructions"
              description="Please contact MCHUSA billing at billing@mchusa.com to arrange payment"
            />
          </Modal>
        );
      })()}

      {/* ── Submit Billing Modal ── */}
      <Modal title="Submit Billing Record" open={billingModal} onOk={() => billingForm.submit()}
        onCancel={() => { setBillingModal(false); billingForm.resetFields(); }}
        okText="Submit" confirmLoading={submitting} destroyOnHidden
 keyboard={true}>
        <Form form={billingForm} layout="vertical" onFinish={handleBilling}>
          <Form.Item name="patient" label="Patient" rules={[{ required: true, message: "Select a patient" }]}>
            <Select placeholder="Select enrolled patient" showSearch optionFilterProp="children">
              {enrolledOpts}
            </Select>
          </Form.Item>
          <Form.Item name="service_date" label="Service Date" rules={[{ required: true, message: "Required" }]}>
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="service_codes" label="ICD-10 Codes (comma-separated)"
            rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="e.g. J06.9, Z00.00" />
          </Form.Item>
          <Form.Item name="payor_type" label="Payor Type" rules={[{ required: true, message: "Required" }]}>
            <Select placeholder="Select payor type">
              <Option value="PRIVATE">Private Insurance</Option>
              <Option value="GOVERNMENT">Government</Option>
              <Option value="SELF_PAY">Self-Pay</Option>
              <Option value="HMO">HMO</Option>
            </Select>
          </Form.Item>
          <Form.Item name="payor_name" label="Payor Name">
            <Input placeholder="Insurance company or HMO name (optional)" />
          </Form.Item>
          <Form.Item name="amount_billed" label="Amount Billed"
            rules={[
              { required: true, message: "Required" },
              { validator: (_, v) => !v || parseFloat(v) > 0 ? Promise.resolve() : Promise.reject("Must be > 0") },
            ]}>
            <Input type="number" min="0.01" step="0.01" prefix="$" placeholder="0.00" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── New Ticket Modal ── */}
      <Modal title="Report an Issue" open={ticketModal}
        onOk={()=>ticketForm.submit()} onCancel={()=>{ setTicketModal(false); ticketForm.resetFields(); }}
        okText="Submit" destroyOnHidden keyboard>
        <Form form={ticketForm} layout="vertical" onFinish={handleCreateTicket}>
          <Form.Item name="subject" label="Subject" rules={[{required:true,message:"Required"}]}>
            <Input placeholder="Brief description of the issue" />
          </Form.Item>
          <Form.Item name="priority" label="Priority" rules={[{required:true,message:"Select priority"}]}>
            <Select placeholder="Select priority">
              <Option value="LOW">Low</Option>
              <Option value="MEDIUM">Medium</Option>
              <Option value="HIGH">High</Option>
              <Option value="CRITICAL">Critical</Option>
            </Select>
          </Form.Item>
          <Form.Item name="description" label="Description" rules={[{required:true,message:"Required"}]}>
            <Input.TextArea rows={4} placeholder="Describe the issue in detail" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Ticket Detail Modal ── */}
      <Modal title="Ticket Details" open={Boolean(ticketDetail)}
        onCancel={()=>setTicketDetail(null)}
        footer={<Button onClick={()=>setTicketDetail(null)}>Close</Button>}
        destroyOnHidden keyboard width={500}>
        {ticketDetail && (
          <div>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <tbody>
                {[
                  ["Subject",  ticketDetail.subject],
                  ["Priority", <Tag color={{LOW:"default",MEDIUM:"blue",HIGH:"orange",CRITICAL:"red"}[ticketDetail.priority]}>{ticketDetail.priority}</Tag>],
                  ["Status",   <Tag color={{OPEN:"blue",IN_PROGRESS:"orange",RESOLVED:"green",CLOSED:"default",ESCALATED:"red"}[ticketDetail.status]??"default"}>{ticketDetail.status?.replace(/_/g," ")}</Tag>],
                  ["Created",  new Date(ticketDetail.created_at).toLocaleString()],
                  ["Updated",  new Date(ticketDetail.updated_at).toLocaleString()],
                ].map(([label,val])=>(
                  <tr key={label} style={{borderBottom:"1px solid #f0f0f0"}}>
                    <td style={{padding:"8px 12px",color:"#8c8c8c",width:"35%"}}>{label}</td>
                    <td style={{padding:"8px 12px"}}>{val}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{marginTop:12}}>
              <Text strong>Description</Text>
              <div style={{marginTop:6,padding:10,background:"#fafafa",borderRadius:4,fontSize:13,color:"#595959"}}>{ticketDetail.description}</div>
            </div>
            {ticketDetail.status === "ESCALATED" && (
              <div style={{marginTop:12,padding:10,background:"#fff1f0",border:"1px solid #ffa39e",borderRadius:4}}>
                <Text strong style={{color:"#cf1322"}}>Escalated to MCHUSA</Text>
                <div style={{marginTop:4,fontSize:13,color:"#595959"}}>
                  Your ticket has been escalated to MCHUSA for direct resolution. You will be notified once it is resolved.
                </div>
              </div>
            )}
            {ticketDetail.resolution_notes && (
              <div style={{marginTop:12}}>
                <Text strong>Resolution Notes</Text>
                <div style={{marginTop:6,padding:10,background:"#f6ffed",border:"1px solid #b7eb8f",borderRadius:4,fontSize:13}}>{ticketDetail.resolution_notes}</div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </Layout>
  );
}
